import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NotificationsIcon from "@mui/icons-material/Notifications";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Popover,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import Icon from "@mui/material/Icon";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Outlet, useNavigate } from "react-router-dom";
import logo from "../assets/logo.png";
import { useNavBarTitle } from "../contexts/NavBarTitleContext";
import { apiRequest } from "../utils/apiRequest";
import { logoutUser } from "../utils/auth";
import { useEventStream } from "../utils/useEventStream";
import { useNotifications } from "../utils/useNotifications";
import { usePermissionTemplate } from "../utils/usePermissionTemplate";
import { useUserSession } from "../utils/useUserSession";
import "./NavBar.css";

export default function NavBar({ children } = {}) {
  const navigate = useNavigate();
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const { user, loading: loadingUser, error: userError } = useUserSession();
  const navTitle = useNavBarTitle();

  // Fetch permission template from backend (not stored client-side)
  const { template: permissionTemplate } = usePermissionTemplate();

  // All companies where user is admin (4)
  const adminCompanyIds = useMemo(() => {
    if (!permissionTemplate) return [];
    return Object.entries(permissionTemplate)
      .filter(([, val]) => val?.user_access_levels?.admin === 4)
      .map(([key]) => {
        const num = Number.parseInt(key, 10);
        return Number.isNaN(num) ? key : String(num);
      });
  }, [permissionTemplate]);

  const isAdmin = adminCompanyIds.length > 0;

  // Notification preferences (per company, session-based)
  const [notificationPrefs, setNotificationPrefs] = useState({});
  const [notificationPrefsLoaded, setNotificationPrefsLoaded] = useState(false);

  const loadNotificationPrefs = useCallback(async () => {
    try {
      const prefsJson = await apiRequest(
        `${process.env.REACT_APP_API_BASE_URL}/notification-preferences/`,
        { method: "GET" }
      );
      setNotificationPrefs(prefsJson?.preferences || {});
    } catch {
      // ignore
    } finally {
      setNotificationPrefsLoaded(true);
    }
  }, []);

  const ready = !loadingUser && !!user;

  const {
    notifications,
    loading: loadingNotifications,
    handleNotificationNew,
    markNotificationsRead,
  } = useNotifications({ isAdmin, ready });

  const [anchorEl, setAnchorEl] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);

  const NOTIF_PAGE_SIZE = 10;
  const [newPage, setNewPage] = useState(1);
  const [readPage, setReadPage] = useState(1);

  const newNotifications = notifications.filter((n) => !n.is_read);
  const readNotifications = notifications.filter((n) => n.is_read);

  const visibleNew = newNotifications.slice(0, newPage * NOTIF_PAGE_SIZE);
  const hasMoreNew = newNotifications.length > visibleNew.length;
  const visibleRead = readNotifications.slice(0, readPage * NOTIF_PAGE_SIZE);
  const hasMoreRead = readNotifications.length > visibleRead.length;

  const enabledAdminCompanyIds = useMemo(() => {
    if (!notificationPrefsLoaded || !isAdmin) return [];
    return adminCompanyIds.filter((cid) => {
      const key = String(cid);
      if (key in notificationPrefs) return !!notificationPrefs[key];
      return true;
    });
  }, [notificationPrefsLoaded, isAdmin, adminCompanyIds, notificationPrefs]);

  const handleBellClick = (e) => {
    setAnchorEl(e.currentTarget);
    setNewPage(1);
    setReadPage(1);
  };
  const handleBellClose = () => setAnchorEl(null);
  const open = Boolean(anchorEl);

  // Load notification prefs once when user session is ready
  useEffect(() => {
    if (ready) loadNotificationPrefs();
  }, [ready, loadNotificationPrefs]);

  useEventStream({
    companyIds: enabledAdminCompanyIds.map((id) => Number(id)),
    enabled: ready && enabledAdminCompanyIds.length > 0,
    onNotificationNew: handleNotificationNew,
  });

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".profile-menu, .dropdown-menu")) setProfileMenuOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    // <div className={`layout ${isSidebarOpen ? "sidebar-expanded" : "sidebar-collapsed"}`}>
    <div className="layout sidebar-collapsed">
      <header className="header navbar">
        <div className="left-section">
          {navTitle.showBack && navTitle.onBack && (
            <Tooltip title="Go back">
              <IconButton
                onClick={navTitle.onBack}
                size="small"
                sx={{
                  mr: 0.5,
                  bgcolor: "rgba(0,0,0,0.06)",
                  "&:hover": { bgcolor: "rgba(0,0,0,0.12)" },
                }}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Link to="/home" className="logo-link">
            <img src={logo} alt="Logo" className="logo-img" />
          </Link>
          {navTitle.title && (
            <>
              <Divider orientation="vertical" flexItem sx={{ mx: 1.5, my: 0.5 }} />
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, color: "#333", whiteSpace: "nowrap" }}
              >
                {navTitle.title}
              </Typography>
            </>
          )}
          {navTitle.showRefresh && navTitle.onRefresh && (
            <Tooltip title="Refresh data">
              <IconButton onClick={navTitle.onRefresh} size="small" color="success" sx={{ ml: 1 }}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </div>

        <div className="profile-menu">
          {/* Show notifications for all authenticated users; while loading, hide to avoid flicker */}
          {!loadingUser && user && isAdmin && (
            <Tooltip title="Notifications">
              <IconButton
                onClick={handleBellClick}
                aria-label="Notifications"
                sx={{ color: "primary.main" }}
              >
                <Badge variant="dot" color="error" invisible={newNotifications.length === 0}>
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            </Tooltip>
          )}

          <Popover
            anchorEl={anchorEl}
            open={open}
            onClose={handleBellClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            transformOrigin={{ vertical: "top", horizontal: "center" }}
            PaperProps={{ sx: { width: 380, maxHeight: 420 } }}
          >
            <Tabs
              value={tabIndex}
              onChange={(_, v) => setTabIndex(v)}
              variant="fullWidth"
              sx={{
                minHeight: 36,
                "& .MuiTab-root": { minHeight: 36, py: 0.5, fontSize: "0.8rem" },
              }}
            >
              <Tab label={`New (${newNotifications.length})`} />
              <Tab label={`Read (${readNotifications.length})`} />
            </Tabs>

            <Box sx={{ overflowY: "auto" }}>
              {tabIndex === 0 && (
                <Box>
                  {loadingNotifications ? (
                    <Typography p={1.5} align="center" color="text.secondary" fontSize="0.85rem">
                      Loading...
                    </Typography>
                  ) : (
                    <>
                      <List disablePadding dense>
                        {visibleNew.map((n) => (
                          <ListItem key={n.id} divider sx={{ py: 0.5, alignItems: "flex-start" }}>
                            <ListItemText
                              primary={n.text}
                              secondary={new Date(n.timestamp).toLocaleString()}
                              primaryTypographyProps={{ fontSize: "0.82rem", lineHeight: 1.3 }}
                              secondaryTypographyProps={{ fontSize: "0.72rem" }}
                              sx={{ pr: 8, my: 0 }}
                            />
                            <ListItemSecondaryAction>
                              <Button
                                size="small"
                                sx={{ fontSize: "0.72rem", minWidth: 0, px: 1 }}
                                onClick={() => markNotificationsRead([n.id])}
                              >
                                Mark Read
                              </Button>
                            </ListItemSecondaryAction>
                          </ListItem>
                        ))}
                      </List>
                      <Box display="flex" justifyContent="center" gap={1} py={0.5}>
                        {hasMoreNew && (
                          <Button
                            size="small"
                            sx={{ fontSize: "0.75rem" }}
                            onClick={() => setNewPage((p) => p + 1)}
                          >
                            Load More
                          </Button>
                        )}
                        {newNotifications.length > 0 && (
                          <Button
                            size="small"
                            sx={{ fontSize: "0.75rem" }}
                            onClick={() => markNotificationsRead(newNotifications.map((n) => n.id))}
                          >
                            Read All
                          </Button>
                        )}
                      </Box>
                      {newNotifications.length === 0 && (
                        <Typography
                          p={1.5}
                          align="center"
                          color="text.secondary"
                          fontSize="0.85rem"
                        >
                          No new notifications
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              )}

              {tabIndex === 1 && (
                <>
                  {loadingNotifications ? (
                    <Typography p={1.5} align="center" color="text.secondary" fontSize="0.85rem">
                      Loading...
                    </Typography>
                  ) : (
                    <>
                      <List disablePadding dense>
                        {visibleRead.map((n) => (
                          <ListItem key={n.id} divider sx={{ py: 0.5 }}>
                            <ListItemText
                              primary={n.text}
                              secondary={new Date(n.timestamp).toLocaleString()}
                              primaryTypographyProps={{ fontSize: "0.82rem", lineHeight: 1.3 }}
                              secondaryTypographyProps={{ fontSize: "0.72rem" }}
                              sx={{ my: 0 }}
                            />
                          </ListItem>
                        ))}
                      </List>
                      {hasMoreRead && (
                        <Box textAlign="center" py={0.5}>
                          <Button
                            size="small"
                            sx={{ fontSize: "0.75rem" }}
                            onClick={() => setReadPage((p) => p + 1)}
                          >
                            Load More
                          </Button>
                        </Box>
                      )}
                      {readNotifications.length === 0 && (
                        <Typography
                          p={1.5}
                          align="center"
                          color="text.secondary"
                          fontSize="0.85rem"
                        >
                          No read notifications
                        </Typography>
                      )}
                    </>
                  )}
                </>
              )}
            </Box>
          </Popover>

          <Tooltip title={user?.name || "Profile"}>
            <IconButton onClick={() => setProfileMenuOpen((prev) => !prev)} sx={{ p: 0.5 }}>
              <Avatar
                src={user?.profilePicture}
                alt={user?.name || "Profile"}
                sx={{ width: 30, height: 30, fontSize: "0.85rem", bgcolor: "#1976d2" }}
              >
                {user?.name?.[0] || "U"}
              </Avatar>
            </IconButton>
          </Tooltip>

          {profileMenuOpen && (
            <div className="dropdown-menu">
              <div className="dropdown-email">{user?.email_address || "user@example.com"}</div>
              <Button
                className="dropdown-item feedback-button"
                startIcon={<Icon>feedback</Icon>}
                onClick={() => {
                  setProfileMenuOpen(false);
                  window.open(
                    "https://forms.gle/2DQiorivkojY8eCd7",
                    "_blank",
                    "noopener,noreferrer"
                  );
                }}
              >
                Feedback Form
              </Button>
              <Button
                className="dropdown-item"
                startIcon={<Icon>person_add</Icon>}
                onClick={() => {
                  setProfileMenuOpen(false);
                  navigate("/add-users");
                }}
              >
                Manage Users
              </Button>
              <Button
                className="dropdown-item settings-button"
                startIcon={<Icon>settings</Icon>}
                onClick={() => {
                  setProfileMenuOpen(false);
                  navigate("/settings");
                }}
              >
                Settings
              </Button>
              <Button
                className="dropdown-item logout-button"
                onClick={() => logoutUser(navigate)}
                startIcon={<Icon>logout</Icon>}
              >
                Logout
              </Button>
            </div>
          )}
        </div>
      </header>

      <main className="main-content">{children || <Outlet />}</main>
    </div>
  );
}
